import { zodResolver } from "@hookform/resolvers/zod";
import { CirclePlus } from "lucide-react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { type FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { invalidateEntityHistory } from "@/api/admin/auditLog";
import {
  type AttributeType,
  type EntityType,
  type InfoProperty,
  PROPERTY_ICON_ACCEPTED_TYPES,
  PropertyIconValidation,
  useCreateNewPropertyMutation,
} from "@/api/admin/properties";
import { commonApi } from "@/api/common";
import { AUDIT_LOG_TYPE_KEYS, QUERY_KEYS } from "@/api/constants";
import { queryClient } from "@/api/queryClient";
import {
  CustomButton,
  CustomCardBody,
  DialogBody,
  ErrorForm,
  SimpleText,
  Subtitle,
} from "@/components/atoms";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { MediaStore, MediaUploader, UploadedFiles } from "@/components/units";
import type { MappedProperty } from "@/modules/SystemTags/components/mapProperty";
import { commonIconProps20 } from "@/utils/constants";

import { InputControl } from "../InputControl";
import { toastError, toastSuccess, toastWarning } from "../Toast";
import { type TagFormType, TagSchema } from "./types";

interface NewTagModalProps {
  scope: EntityType;
  type: AttributeType;
  tagList?: MappedProperty[];
}

const NewTagModal: FC<NewTagModalProps> = observer(
  ({ scope, type, tagList }) => {
    const [isOpen, setOpen] = useState(false);

    const mediaStore = useLocalObservable(
      () => new MediaStore(undefined, PropertyIconValidation)
    );
    const [errorMedia, setErrorMedia] = useState<string | null>(null);

    const form = useForm<TagFormType>({
      resolver: zodResolver(TagSchema),
      defaultValues: {
        nameAr: "",
        nameEn: "",
      },
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    const {
      control,
      handleSubmit,
      formState: { isValid },
    } = form;

    const handleCancel = () => {
      form.reset({ nameEn: "", nameAr: "" });
      setOpen(false);
      mediaStore.resetStore();
      setErrorMedia(null);
    };

    const { mutate, isPending } = useCreateNewPropertyMutation();

    const onSubmit = async (data: TagFormType) => {
      const exists = tagList?.some(
        tag =>
          tag.nameEn.trim().toLowerCase() === data.nameEn.trim().toLowerCase()
      );

      if (exists) {
        toastWarning("A Tag with this name already exists.");
        return;
      }

      let finalMediaItemId: string | undefined = undefined;

      if (mediaStore.mediaFiles.length > 0) {
        const mediaFile = mediaStore.mediaFiles[0];

        if (mediaFile.file) {
          const response = await commonApi.mediaUpload(mediaFile.file);
          finalMediaItemId = response.id;
        }
      } else {
        finalMediaItemId = undefined;
      }

      const payload = {
        nameEn: data.nameEn,
        nameAr: data.nameAr,
        scope,
        type,
        mediaId: finalMediaItemId,
      } as InfoProperty;

      mutate(payload, {
        onSuccess: () => {
          toastSuccess(`Tag ${data.nameEn} was created successfully!`);
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.property],
          });
          invalidateEntityHistory({
            scope: AUDIT_LOG_TYPE_KEYS.TAGS,
          });
          handleCancel();

          mediaStore.resetStore();
        },
        onError: err => {
          toastError(
            err?.message || "Failed to create tag.",
            () => onSubmit(data),
            "Error"
          );
        },
      });
    };

    return (
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <CustomButton
            size="sm"
            type="button"
            variant="link"
            data-testid="system-add-tag-button"
            className="px-0!"
            onClick={() => setOpen(true)}
          >
            <CirclePlus {...commonIconProps20} color="#155FCC" />
            Add new
          </CustomButton>
        </DialogTrigger>

        <DialogBody title="New tag" classes="gap-2">
          <div className="flex flex-col gap-5 pt-2">
            <FormProvider {...form}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <CustomCardBody classes="pb-5 px-0">
                  <div className="flex flex-col align-center">
                    <InputControl<TagFormType>
                      formControl={control}
                      label="Tag name (English)"
                      nameField="nameEn"
                      data-testid="new-tag-name-english"
                    />
                    <InputControl<TagFormType>
                      formControl={control}
                      label="Tag name (Arabic)"
                      nameField="nameAr"
                      data-testid="new-tag-name-arabic"
                      dir="rtl"
                    />
                  </div>

                  <div>
                    <Subtitle classes="mb-1" title="Upload icon" />
                    <SimpleText classes="mb-6">
                      <span className="font-bold">Icon requirements:</span> use
                      an SVG. Keep it under 1 MB for faster loading. Dimensions:
                      (40X40px).
                    </SimpleText>
                    <div className="flex gap-2">
                      {mediaStore.mediaFiles.map((media, index) => (
                        <UploadedFiles
                          media={media}
                          key={index}
                          index={index}
                          mediaStore={mediaStore}
                        />
                      ))}
                      <MediaUploader
                        acceptedTypes={PROPERTY_ICON_ACCEPTED_TYPES}
                        sizeLimits={{
                          max: PropertyIconValidation.vector?.maxSize,
                          min: PropertyIconValidation.vector?.minSize,
                        }}
                        disabled={mediaStore.isImageLimitReached}
                        mediaStore={mediaStore}
                        onError={setErrorMedia}
                      />
                    </div>
                    {errorMedia && <ErrorForm message={errorMedia} />}
                  </div>
                </CustomCardBody>
                <div className="flex justify-between gap-3">
                  <CustomButton
                    variant="light"
                    type="button"
                    className="flex-1"
                    data-testid="invite-user-cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </CustomButton>

                  <CustomButton
                    type="submit"
                    className="flex-1"
                    data-testid="new-tag-submit-button"
                    disabled={!isValid}
                    isPending={isPending}
                  >
                    Save
                  </CustomButton>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogBody>
      </Dialog>
    );
  }
);

export default NewTagModal;
